import type { DocumentChild } from '@protoss/doc';
import * as React from 'react';
import { apiFetch } from '../lib/apiClient';

export interface DocSectionState {
  /** Structured body of the matched section (heading paragraph excluded). */
  children: DocumentChild[] | null;
  /** False once the fetch resolved but no .docx section matched the heading. */
  found: boolean;
  loading: boolean;
  error: boolean;
}

interface DocSectionResponse {
  found: boolean;
  heading?: string;
  level?: number;
  children?: DocumentChild[];
}

/**
 * Fetches one section of a .docx workspace asset as structured contract children
 * (paragraphs/tables/lists), located by heading - so the caller can render clean,
 * normalized HTML instead of a faithful docx render. Lazy: nothing is fetched
 * until `active` is true (the visible form tab). Best-effort: a non-.docx asset,
 * an unmatched heading, or a failed fetch resolves to `found: false` so callers
 * fall back to a plain-text preview.
 */
export function useDocSection(
  assetUuid: string | null | undefined,
  heading: string | null | undefined,
  active: boolean
): DocSectionState {
  const [state, setState] = React.useState<DocSectionState>({
    children: null,
    found: false,
    loading: false,
    error: false,
  });

  React.useEffect(() => {
    if (!active || !assetUuid || !heading) {
      setState({ children: null, found: false, loading: false, error: false });
      return;
    }

    let cancelled = false;
    setState({ children: null, found: false, loading: true, error: false });
    void (async () => {
      try {
        const result = await apiFetch<DocSectionResponse>(
          `/api/workspace-assets/${assetUuid}/doc-section?heading=${encodeURIComponent(heading)}`
        );
        if (cancelled) return;
        setState({
          children: result.found ? (result.children ?? []) : null,
          found: result.found,
          loading: false,
          error: false,
        });
      } catch {
        if (!cancelled) setState({ children: null, found: false, loading: false, error: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [assetUuid, heading, active]);

  return state;
}
